import { AlpineComponent } from "alpinejs";

type ScrollSlider = AlpineComponent<{
	canGoBack: boolean;
	canGoForward: boolean;
	scrollWidth: number;
	scrollMargin: number;
	goBack: () => void;
	goForward: () => void;
	update: () => void;
}>;

interface ScrollSliderOptions {
	scrollStep?: number;
	scrollMargin?: number;
}

export default (options: ScrollSliderOptions): ScrollSlider => {
	const defaults = {
		scrollWidth: 300,
		scrollMargin: 50,
	};
	/**
	 * Small tolerance to avoid floating point jitter near 0 / max
	 */
	const scrollEdgeTolerance = 0.5;
	const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
	let element: HTMLElement | undefined;
	let rafPending = false;

	function go(stepCount: number) {
		if (!element) {
			return;
		}

		const behavior = prefersReducedMotion ? "auto" : "smooth";
		element.scrollBy({ left: stepCount * component.scrollWidth, behavior: behavior });

		// Immediate state + schedule a follow-up after frame (captures smooth scroll progression)
		component.update();
		requestUpdate();
	}

	function requestUpdate() {
		if (rafPending) {
			return;
		}
		rafPending = true;
		requestAnimationFrame(() => {
			rafPending = false;
			component.update();
		});
	}

	const component: ScrollSlider = {
		canGoBack: false,
		canGoForward: false,
		scrollWidth: options.scrollStep ?? defaults.scrollWidth,
		scrollMargin: options.scrollMargin ?? defaults.scrollMargin,
		init() {
			element = this.$refs.element as HTMLElement;
			if (!element) {
				return;
			}

			this.update();
			// Throttled updates on resize & native scroll
			window.addEventListener("resize", requestUpdate, { passive: true });
		},
		destroy() {
			window.removeEventListener("resize", requestUpdate);
		},
		goBack() {
			go(-1);
		},
		goForward() {
			go(1);
		},
		update() {
			if (!element) {
				return;
			}

			const maxScroll = element.scrollWidth - element.clientWidth;
			const remaining = maxScroll - element.scrollLeft;

			this.canGoBack = element.scrollLeft > 0;
			this.canGoForward = remaining > this.scrollMargin + scrollEdgeTolerance;
		},
	};

	return component;
};
